
import React, { useState, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message, Role, Attachment, MessageDensity, User as UserType, GlobalConfig } from '../types';
import { User, AlertCircle, Copy, Check, FileText, ThumbsUp, ThumbsDown, Eye, RefreshCw, BrainCircuit, ChevronDown, ChevronUp, Pin, Zap, Sparkles, Download, Gauge, Info } from 'lucide-react';
import { getRankGradient } from '../utils/rankUtils';

// Lazy load charts - only loaded when message contains chart data
const LazyChartRenderer = lazy(() => import('./LazyChartRenderer'));
import { ChartSkeleton } from './LazyChartRenderer';

interface FeedbackRewardInfo {
  rewarded: boolean;
  amount: number;
}

// Regex to detect interactive quiz data
const INTERACTIVE_QUIZ_REGEX = /<QUIZ>([\s\S]*)/;

interface ChatMessageProps {
  message: Message;
  isLast?: boolean;
  density?: MessageDensity;
  isDarkMode?: boolean;
  user?: UserType; // Current user for rank colors
  config?: GlobalConfig; // Config for rank colors
  onFeedback?: (messageId: string, type: 'up' | 'down') => Promise<FeedbackRewardInfo | void>;
  onRegenerate?: () => void;
  onPreview?: (attachment: Attachment) => void;
  isPinned?: boolean;
  onTogglePin?: (messageId: string) => void;
  onAIPersonaClick?: () => void;
  onExport?: (message: Message) => void; // Export single message
  hasBeenRewarded?: boolean; // Whether this message has already been rewarded
  userName?: string; // For avatar display
  onOpenQuiz?: (quiz: any) => void; // Support for opening interactive quizzes
  showTimestamps?: boolean; // Whether to show message timestamps
  showAvatars?: boolean; // Whether to show user/AI avatars
}

// Enhanced content formatter with better mobile support
const formatComputation = (content: string): string => {
  // Keep quiz data visible in chat - user preference
  let result = content;

  // Fix common markdown issues
  // 1. Ensure proper spacing around headers
  result = result.replace(/([^\n])(\n#{1,6}\s)/g, '$1\n$2');

  // 2. Fix improperly formatted lists
  result = result.replace(/([^\n])(\n[-*]\s)/g, '$1\n$2');

  // 3. Ensure code blocks have proper line breaks
  result = result.replace(/([^\n])(\n```)/g, '$1\n$2');
  result = result.replace(/(```\n?)([^\n])/g, '$1\n$2');

  return result;
};

// Pre-computed density styles - Comfortable spacing with larger avatars
const DENSITY_STYLES = {
  compact: {
    mb: 'mb-4',
    p: 'p-2.5 sm:p-3',
    avatarSize: 'w-12 h-12 sm:w-13 sm:h-13',
    aiAvatarSize: 'w-12 h-12 sm:w-13 sm:h-13',
    gap: 'gap-3',
    textSize: 'text-xs sm:text-sm',
    iconSize: 14,
    chartHeight: 'h-48 sm:h-56'
  },
  spacious: {
    mb: 'mb-6',
    p: 'p-4 sm:p-5',
    avatarSize: 'w-14 h-14 sm:w-16 sm:h-16',
    aiAvatarSize: 'w-14 h-14 sm:w-16 sm:h-16',
    gap: 'gap-4',
    textSize: 'text-base sm:text-lg',
    iconSize: 22,
    chartHeight: 'h-80 sm:h-96'
  },
  comfortable: {
    mb: 'mb-5',
    p: 'p-3 sm:p-4',
    avatarSize: 'w-12 h-12 sm:w-14 sm:h-14',
    aiAvatarSize: 'w-12 h-12 sm:w-14 sm:h-14',
    gap: 'gap-3',
    textSize: 'text-sm sm:text-base',
    iconSize: 18,
    chartHeight: 'h-64 sm:h-72'
  }
} as const;

// Chart data regex - compiled once outside component
const CHART_REGEX = /```json:chart([\s\S]*?)```/;

// Persona-based bubble colors for AI messages - Solid colors
const PERSONA_COLORS = {
  standard: { // Assistant = Blue
    darkBg: 'bg-blue-900/90',
    darkBorder: 'border-blue-500/30',
    darkHoverBorder: 'hover:border-blue-400/50',
    darkAccent: 'border-l-blue-500',
    lightBg: 'bg-blue-50',
    lightBorder: 'border-blue-200/70',
    lightHoverBorder: 'hover:border-blue-300',
    lightAccent: 'border-l-blue-500',
    avatarBg: 'from-blue-500 to-blue-600',
    avatarGlow: 'rgba(59, 130, 246, 0.3)',
  },
  tutor: { // Tutor = Green
    darkBg: 'bg-emerald-900/90',
    darkBorder: 'border-emerald-500/30',
    darkHoverBorder: 'hover:border-emerald-400/50',
    darkAccent: 'border-l-emerald-500',
    lightBg: 'bg-emerald-50',
    lightBorder: 'border-emerald-200/70',
    lightHoverBorder: 'hover:border-emerald-300',
    lightAccent: 'border-l-emerald-500',
    avatarBg: 'from-emerald-500 to-emerald-600',
    avatarGlow: 'rgba(16, 185, 129, 0.3)',
  },
  professional: { // Professional = Yellow/Amber
    darkBg: 'bg-amber-900/90',
    darkBorder: 'border-amber-500/30',
    darkHoverBorder: 'hover:border-amber-400/50',
    darkAccent: 'border-l-amber-500',
    lightBg: 'bg-amber-50',
    lightBorder: 'border-amber-200/70',
    lightHoverBorder: 'hover:border-amber-300',
    lightAccent: 'border-l-amber-500',
    avatarBg: 'from-amber-500 to-orange-500',
    avatarGlow: 'rgba(251, 191, 36, 0.3)',
  },
} as const;

// Get persona colors with fallback
const getPersonaColors = (persona?: string) => {
  if (persona && persona in PERSONA_COLORS) {
    return PERSONA_COLORS[persona as keyof typeof PERSONA_COLORS];
  }
  return PERSONA_COLORS.tutor; // Default to tutor (green)
};

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isLast, density = 'comfortable', isDarkMode = true, user, config, onFeedback, onRegenerate, onPreview, isPinned = false, onTogglePin, onAIPersonaClick, onExport, hasBeenRewarded = false, onOpenQuiz, showTimestamps = true, showAvatars = true }) => {
  const isUser = message.role === Role.USER;
  const isSystem = message.role === Role.SYSTEM;
  const isError = message.isError;

  // Special render for system messages (commands, notifications, etc.)
  if (isSystem) {
    return (
      <div className={`w-full px-4 py-3 my-2 animate-in fade-in slide-in-from-top-2 duration-300`}>
        <div className={`max-w-2xl mx-auto rounded-xl p-4 ${isDarkMode
          ? 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
          : 'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
              }`}>
              <BrainCircuit size={16} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
            </div>
            <div className={`flex-1 min-w-0 prose prose-sm max-w-none ${isDarkMode
              ? 'prose-invert prose-headings:text-blue-300 prose-strong:text-blue-200 prose-code:text-blue-300'
              : 'prose-headings:text-blue-900 prose-strong:text-blue-800 prose-code:text-blue-700'
              }`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const [copied, setCopied] = useState(false);
  const [isThinkingOpen, setIsThinkingOpen] = useState(false);

  // Get user rank gradient for chat colors - with persistence to prevent flickering
  const lastValidGradientRef = useRef<{ from: string; to: string } | null>(null);

  const userGradient = useMemo(() => {
    if (!isUser) {
      return { from: '#3b82f6', to: '#2563eb' }; // Default blue for non-user messages
    }

    // Try to get gradient from user and config
    if (user && config) {
      const gradient = getRankGradient(user, config);
      lastValidGradientRef.current = gradient; // Cache it
      return gradient;
    }

    // If user exists but config is missing, determine directly from user's tier
    if (user) {
      const tier = user.accountTier || (user.isMax || user.isMaxTier ? 'max' : user.isPremium ? 'premium' : 'standard');
      const tierGradients: Record<string, { from: string; to: string }> = {
        max: { from: '#a855f7', to: '#ec4899' },      // Purple to Pink
        premium: { from: '#fbbf24', to: '#f59e0b' },  // Yellow to Amber
        standard: { from: '#94a3b8', to: '#64748b' }, // Slate gradient
      };
      const gradient = tierGradients[tier] || tierGradients.standard;
      lastValidGradientRef.current = gradient;
      return gradient;
    }

    // Use last valid gradient if available (prevents flickering)
    if (lastValidGradientRef.current) {
      return lastValidGradientRef.current;
    }

    // Ultimate fallback to blue
    return { from: '#3b82f6', to: '#2563eb' };
  }, [isUser, user, config]);

  // Feedback reward state
  const [showRewardAnimation, setShowRewardAnimation] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState<'up' | 'down' | null>(null);
  const [localRewarded, setLocalRewarded] = useState(hasBeenRewarded);
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);

  // Handle feedback with reward animation
  const handleFeedbackClick = useCallback(async (type: 'up' | 'down') => {
    if (!onFeedback || feedbackLoading) return;

    // If already has feedback of the same type, just toggle off (no reward)
    if (message.feedback === type) {
      onFeedback(message.id, type);
      return;
    }

    setFeedbackLoading(type);

    try {
      const result = await onFeedback(message.id, type);

      // If we got a reward, show the animation
      if (result && result.rewarded && result.amount > 0) {
        setRewardAmount(result.amount);
        setShowRewardAnimation(true);
        setLocalRewarded(true);

        // Hide animation after delay
        setTimeout(() => {
          setShowRewardAnimation(false);
        }, 2500);
      }
    } catch (error) {
      console.error('Feedback error:', error);
    } finally {
      setFeedbackLoading(null);
    }
  }, [onFeedback, message.id, message.feedback, feedbackLoading]);

  // Density Styles - now using pre-computed constant
  const styles = DENSITY_STYLES[density] || DENSITY_STYLES.comfortable;

  // Extract chart data if present in json:chart block and preprocess HTML
  const { content, chartData, quizData } = useMemo(() => {
    let extractedChartData: any | null = null;
    let processedContent = message.content;

    // ⚡ STREAMING OPTIMIZATION: During streaming, skip heavy processing (table sanitization,
    // HTML cleanup, regex chains) and just do minimal formatting. Full processing runs on final render.
    const isCurrentlyStreaming = message.isStreaming;

    // ✅ MOBILE/APK FIX: Limit extremely long responses to prevent gray screen and artifacts
    // Detect if running on mobile/APK environment
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isCapacitor = !!(window as any).Capacitor;

    // ✅ FIX: Increased limits to prevent messages from disappearing
    // Use progressive limits - allow longer content but with better handling
    const MAX_CONTENT_LENGTH = (isMobile || isCapacitor) ? 100000 : 200000; // 100KB for mobile, 200KB for desktop
    const wasTruncated = processedContent.length > MAX_CONTENT_LENGTH;

    if (wasTruncated) {
      // 🎯 Smart truncation: Try to end at paragraph or sentence boundary
      let truncateAt = MAX_CONTENT_LENGTH;
      const searchRange = processedContent.substring(MAX_CONTENT_LENGTH - 1000, MAX_CONTENT_LENGTH);

      // Try to find last paragraph break
      const lastParagraph = searchRange.lastIndexOf('\n\n');
      if (lastParagraph !== -1) {
        truncateAt = MAX_CONTENT_LENGTH - 1000 + lastParagraph;
      } else {
        // Try to find last sentence
        const lastSentence = Math.max(
          searchRange.lastIndexOf('. '),
          searchRange.lastIndexOf('! '),
          searchRange.lastIndexOf('? ')
        );
        if (lastSentence !== -1) {
          truncateAt = MAX_CONTENT_LENGTH - 1000 + lastSentence + 2; // Include punctuation + space
        }
      }

      const truncationMessage = (isMobile || isCapacitor)
        ? '\n\n---\n**⚠️ Response truncated for mobile optimization. Scroll to see more content.**'
        : '\n\n---\n**⚠️ Response truncated due to length. The full response was too long to display.**';

      processedContent = processedContent.substring(0, truncateAt).trim() + truncationMessage;
    }

    // Extract quiz data - check message.quiz first, then fallback to regex extraction
    let extractedQuizData: any | null = null;

    // Priority 1: Use message.quiz if available (set by App.tsx when quiz/exam is generated)
    if (message.quiz) {
      extractedQuizData = message.quiz;
      // Also remove any <QUIZ> tag from content to prevent showing raw JSON
      processedContent = processedContent.replace(INTERACTIVE_QUIZ_REGEX, '').trim();
    }
    // Priority 2: Extract from <QUIZ> tags in content (legacy/inline quizzes)
    else {
      const quizMatch = processedContent.match(INTERACTIVE_QUIZ_REGEX);
      if (quizMatch && quizMatch[1]) {
        try {
          const potentialJson = quizMatch[1].trim();
          // Only attempt to parse if it looks like a complete JSON object (ends with })
          // This prevents console spam during streaming
          if (potentialJson.endsWith('}')) {
            extractedQuizData = JSON.parse(potentialJson);
            // Remove the quiz JSON from the display content
            processedContent = processedContent.replace(INTERACTIVE_QUIZ_REGEX, '').trim();
          }
        } catch (e) {
          // Silent failure is expected during streaming
        }
      }
    }

    // Extract chart data using pre-compiled regex
    const match = processedContent.match(CHART_REGEX);
    if (match && match[1]) {
      try {
        extractedChartData = JSON.parse(match[1]);
        processedContent = processedContent.replace(match[0], '').trim();
      } catch (e) {
        // Ignore parsing errors
      }
    }

    // Preprocess HTML for better markdown rendering
    if (!isUser && !isError) {
      // ⚡ During streaming: only do minimal safe cleanup. Full processing on final render.
      if (isCurrentlyStreaming) {
        // Minimal cleanup only — skip heavy regex chains, table sanitizer, and formatComputation
        processedContent = processedContent
          .replace(/<br\s*\/?>/gi, '\n\n')
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<\/?(span|div|p|font)[^>]*>/gi, '');
      } else {
      // Replace <br> with newlines
      processedContent = processedContent.replace(/<br\s*\/?>/gi, '\n\n');

      // Handle styled divs - convert to highlighted blocks
      processedContent = processedContent.replace(/<div[^>]*style="([^"]*)"[^>]*>(.*?)<\/div>/gi, (match, style, innerContent) => {
        const cleanContent = innerContent.replace(/<[^>]+>/g, '').trim();
        if (/border/i.test(style) && /text-align:\s*center/i.test(style)) {
          return `\n\n> **${cleanContent}**\n\n`;
        }
        return cleanContent;
      });

      // ✅ Sanitize dangerous and malformed HTML
      processedContent = processedContent
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
        .replace(/<object[^>]*>.*?<\/object>/gi, '')
        .replace(/<embed[^>]*>/gi, '')
        .replace(/<\/?(span|div|p|font)[^>]*>/gi, '')
        // ✅ Remove malformed or broken HTML tags that cause artifacts
        .replace(/<[^>]{200,}>/g, '') // Remove extremely long tag attributes
        .replace(/<\/?[^a-zA-Z][^>]*>/g, ''); // Remove tags starting with non-letters

      // ═══════════════════════════════════════════════════════════════════
      // AGGRESSIVE TABLE SANITIZER - Handles malformed AI output
      // ═══════════════════════════════════════════════════════════════════

      const sanitizeTable = (content: string): string => {
        // 📊 TABLE PROCESSING CONFIGURATION
        const TABLE_CONFIG = {
          MAX_COLUMNS: 7,            // Max columns (6-7 is readable on mobile)
          WARN_COLUMNS: 5,           // Warn threshold for mobile
          MAX_TABLES: 25,            // Max tables per response
          MAX_CELL_LENGTH: 200,      // Max chars per cell (increased for better content)
          MAX_ROW_LENGTH: 2500,      // Max chars per row (increased)
          MAX_CELL_LENGTH_SEVERE: 1000, // Severe malformation threshold
          REPETITION_THRESHOLD: 0.7, // Uniqueness ratio for deduplication
        };

        const lines = content.split('\n');
        const result: string[] = [];
        let tableBuffer: string[] = [];
        let tableCount = 0;  // Track number of tables processed

        const isTableLine = (line: string) => /^\s*\|.*\|/.test(line.trim());
        const isSeparator = (line: string) => /^\s*\|[\s:-]+\|/.test(line) || /^\s*\|(\s*-+\s*\|)+/.test(line);

        const cleanCell = (cell: string): string => {
          let c = cell.trim();
          // Remove repeated garbage characters
          c = c.replace(/(.)\1{10,}/g, '$1$1...');
          // Remove non-printable
          c = c.replace(/[^\x20-\x7E\u00A0-\u00FF\u0100-\u017F]/g, '');
          // Remove pipe characters within cells (would break table)
          c = c.replace(/\|/g, '│');
          // Truncate to configured max for mobile
          if (c.length > TABLE_CONFIG.MAX_CELL_LENGTH) {
            c = c.substring(0, TABLE_CONFIG.MAX_CELL_LENGTH - 3) + '...';
          }
          return c;
        };

        const convertTableToPlainText = (buffer: string[]): string[] => {
          // Convert malformed table to readable plain text
          const converted: string[] = ['', '**Table Content:**', ''];

          buffer.forEach((line, idx) => {
            if (isSeparator(line)) return; // Skip separator

            const cells = line.split('|')
              .map(c => c.trim())
              .filter(c => c.length > 0);

            if (cells.length > 0) {
              if (idx === 0) {
                // Header
                converted.push('**' + cells.join(' | ') + '**');
                converted.push('---');
              } else {
                // Data row
                converted.push('• ' + cells.join(' | '));
              }
            }
          });

          converted.push('');
          return converted;
        };

        const flushTable = () => {
          if (tableBuffer.length === 0) return;

          // Check table count limit (prevent infinite loops)
          if (tableCount >= TABLE_CONFIG.MAX_TABLES) {
            result.push('\n---\n**⚠️ Additional tables simplified for readability**\n---\n');
            result.push(...convertTableToPlainText(tableBuffer));
            tableBuffer = [];
            return;
          }

          // Deduplicate repeating rows (common AI generation issue)
          const uniqueRows = new Set(tableBuffer.map(r => r.trim()));
          if (tableBuffer.length > 3 && uniqueRows.size < tableBuffer.length * TABLE_CONFIG.REPETITION_THRESHOLD) {
            const seen = new Set<string>();
            tableBuffer = tableBuffer.filter(row => {
              const key = row.trim();
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          }

          // Handle incomplete tables silently (common with AI generation)
          const nonSeparatorRows = tableBuffer.filter(l => !isSeparator(l));
          if (nonSeparatorRows.length <= 1) {
            // Silently convert incomplete tables to formatted text (no console spam)
            result.push(...convertTableToPlainText(tableBuffer));
            tableBuffer = [];
            return;
          }

          // Check if table is severely malformed
          const hasSuperLongRow = tableBuffer.some(l => l.length > TABLE_CONFIG.MAX_ROW_LENGTH);
          const hasSuperLongCell = tableBuffer.some(l => {
            const cells = l.split('|');
            return cells.some(c => c.trim().length > TABLE_CONFIG.MAX_CELL_LENGTH_SEVERE);
          });

          // If severely malformed, convert to plain text silently
          if (hasSuperLongRow || hasSuperLongCell) {
            result.push(...convertTableToPlainText(tableBuffer));
            tableBuffer = [];
            return;
          }

          // Try to fix table structure
          const fixedBuffer: string[] = [];
          let columnCount = 0;

          // Determine column count from first row
          if (tableBuffer.length > 0) {
            const firstCells = tableBuffer[0].split('|').filter(c => c.trim());
            columnCount = firstCells.length;

            // Check column count limits silently
            if (columnCount > TABLE_CONFIG.MAX_COLUMNS) {
              result.push(...convertTableToPlainText(tableBuffer));
              tableBuffer = [];
              tableCount++;
              return;
            }
            // High column count is handled by CSS now
          }

          for (let i = 0; i < tableBuffer.length; i++) {
            const line = tableBuffer[i];

            if (isSeparator(line)) {
              fixedBuffer.push(line);
              continue;
            }

            const cells = line.split('|');
            const cleanedCells = cells.map((c, idx) => {
              if (idx === 0 || idx === cells.length - 1) return '';
              return ` ${cleanCell(c)} `;
            });

            // Normalize column count
            const dataCells = cleanedCells.filter((c, idx) => idx > 0 && idx < cleanedCells.length - 1);

            // ✅ Skip completely empty rows
            const hasContent = dataCells.some(c => c.trim().length > 0);
            if (!hasContent && i > 0) {
              continue; // Skip empty data rows
            }

            while (dataCells.length < columnCount) {
              dataCells.push(' ');
            }
            if (dataCells.length > columnCount) {
              dataCells.length = columnCount;
            }

            fixedBuffer.push('|' + dataCells.join('|') + '|');
          }

          // Check if table has separator
          const hasSep = fixedBuffer.some(l => isSeparator(l));
          if (!hasSep && fixedBuffer.length >= 1) {
            // Add separator after header
            const sep = '|' + Array(columnCount).fill(' --- ').join('|') + '|';
            fixedBuffer.splice(1, 0, sep);
          }

          // Add blank line before table if needed
          if (result.length > 0 && result[result.length - 1].trim() !== '') {
            result.push('');
          }
          result.push(...fixedBuffer);
          result.push(''); // Blank after
          tableBuffer = [];
          tableCount++;  // Increment table counter
        };

        for (const line of lines) {
          const trimmed = line.trim();

          if (isTableLine(trimmed)) {
            tableBuffer.push(trimmed);
          } else {
            // Non-table line
            if (tableBuffer.length > 0) {
              flushTable();
            }
            result.push(line);
          }
        }

        // Flush remaining
        if (tableBuffer.length > 0) {
          flushTable();
        }

        return result.join('\n');
      };

      processedContent = sanitizeTable(processedContent);

      // ✅ Final cleanup: remove excessive blank lines and corrupted characters
      processedContent = processedContent
        .replace(/\n{3,}/g, '\n\n')
        // Remove null bytes and control characters (except newlines/tabs)
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
        // Remove replacement characters () that indicate encoding issues
        .replace(/\uFFFD/g, '')
        // Fix multiple spaces
        .replace(/ {3,}/g, '  ');

      // Format computations with better structure
      processedContent = formatComputation(processedContent);

      // ✅ Final length check after all processing - STRICT for mobile
      const finalMaxLength = (isMobile || isCapacitor) ? MAX_CONTENT_LENGTH : MAX_CONTENT_LENGTH * 1.2;
      if (processedContent.length > finalMaxLength) {
        console.warn('[ChatMessage] Content still too long after processing, forcing truncation');
        const truncMsg = (isMobile || isCapacitor)
          ? '\n\n---\n**⚠️ Response truncated for mobile. Use desktop for full response.**'
          : '\n\n---\n**⚠️ Response was too long and has been truncated.**';
        processedContent = processedContent.substring(0, MAX_CONTENT_LENGTH) + truncMsg;
      }
      } // end else (non-streaming heavy processing)
    }

    return { content: processedContent, chartData: extractedChartData, quizData: extractedQuizData };
  }, [message.content, message.isStreaming, isUser, isError]);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Lazy chart rendering - only loads Recharts when chartData exists
  const renderChart = () => {
    if (!chartData || !chartData.data) return null;

    return (
      <Suspense fallback={<ChartSkeleton isDarkMode={isDarkMode} />}>
        <LazyChartRenderer
          chartData={chartData}
          isDarkMode={isDarkMode}
          density={density}
        />
      </Suspense>
    );
  };

  const renderQuizButton = () => {
    if (!quizData || !onOpenQuiz) return null;

    const isExam = quizData.type === 'exam';
    // Determine if this is a saved quiz (from message.quiz) or newly generated (from <QUIZ> tag)
    const isFromSavedSession = !!message.quiz;

    const title = isExam
      ? (isFromSavedSession ? 'Mock Exam Saved' : 'Mock Exam Available')
      : (isFromSavedSession ? 'Quiz Saved' : 'Interactive Quiz Available');
    const actionText = isFromSavedSession
      ? (isExam ? 'Retake Exam' : 'Retake Quiz')
      : (isExam ? 'Open Exam' : 'Open Quiz');
    const itemLabel = isExam ? 'Items' : 'Questions';

    return (
      <div className="my-4">
        <button
          onClick={() => onOpenQuiz(quizData)}
          className={`flex items-center gap-3 px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto text-left group relative z-10 cursor-pointer ${isExam
            ? (isDarkMode
              ? 'bg-gradient-to-br from-pink-600 to-rose-700 hover:from-pink-500 hover:to-rose-600 text-white'
              : 'bg-gradient-to-br from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-white')
            : (isDarkMode
              ? 'bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white'
              : 'bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white')
            }`}
        >
          <div className="bg-white/20 p-2 rounded-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-sm sm:text-base">{title}</h4>
            <p className="text-xs sm:text-sm text-white/90">Click to {actionText.toLowerCase()}: "{quizData.title || (isExam ? 'Mock Exam' : 'Practice Quiz')}"</p>
          </div>
          <div className="ml-auto bg-white/20 px-3 py-1 rounded-full text-xs font-medium">
            {quizData.questions?.length || 0} {itemLabel}
          </div>
        </button>
      </div>
    );
  };

  return (
    <article
      id={`message-${message.id}`}
      className={`
        flex w-full ${styles.mb} px-1 sm:px-2
        ${isUser
          ? 'justify-end animate-in fade-in duration-200 ease-out'
          : 'justify-start animate-in fade-in duration-200 ease-out'
        }
        ${isPinned ? 'bg-amber-500/5 dark:bg-amber-500/10 rounded-xl' : ''}
        message-bubble
      `}
      style={{
        animationFillMode: 'both'
      }}
      aria-label={isUser ? "User message" : "AI response"}
    >
      <div className={`flex w-full ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start ${styles.gap}`}>

        {/* Avatar - Clean and Simple */}
        {showAvatars && (
        <button
          onClick={!isUser && !isError ? onAIPersonaClick : undefined}
          disabled={isUser || isError}
          className={`flex-shrink-0 rounded-2xl flex items-center justify-center overflow-hidden transition-all duration-200 ease-out ${isUser
            ? `${styles.avatarSize} text-white border-2 shadow-lg`
            : isError
              ? `${styles.avatarSize} bg-red-100 text-red-600 border-2 border-white shadow-lg`
              : `${styles.aiAvatarSize} hover:scale-105 cursor-pointer active:scale-95`
            }`}
          style={isUser ? {
            background: `linear-gradient(135deg, ${userGradient.from}, ${userGradient.to})`,
            borderColor: userGradient.from
          } : undefined}
          aria-label={!isUser && !isError ? "Change AI persona" : undefined}
          title={!isUser && !isError ? "Click to change AI persona" : undefined}
        >
          {isUser ? (
            <User size={styles.iconSize + 2} className="drop-shadow-sm" />
          ) : isError ? (
            <AlertCircle size={styles.iconSize} className="animate-pulse" />
          ) : (
            <img
              src="/robot-luca.png"
              onError={(e) => {
                // Fallback chain: try robot-luca.png from different paths
                const target = e.currentTarget;
                if (target.src.includes('/robot-luca.png') && !target.src.includes('./')) {
                  target.src = './robot-luca.png';
                } else if (!target.src.includes('app-icon')) {
                  target.src = '/app-icon.png';
                }
              }}
              alt="Luca AI"
              className="w-full h-full object-contain rounded-xl transition-transform duration-300 hover:scale-105"
              loading="lazy"
            />
          )}
        </button>
        )}

        {/* Message Container - ✅ MOBILE/APK FIX: Ensure container doesn't clip content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-full min-w-0`} style={{
          overflow: 'visible', // Ensure content is never clipped
          minHeight: 'auto' // Allow natural height
        }}>

          {/* Interactive Quiz Button */}
          {!isUser && renderQuizButton()}

          {/* Budget Adjustment Notice */}
          {!isUser && !isError && message.budgetAdjusted && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs mb-2 ${isDarkMode
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}>
              <Gauge size={14} className="flex-shrink-0" />
              <span>Response optimized for {message.budgetAdjusted.toLocaleString()} token budget</span>
              <div className="group relative ml-auto cursor-help">
                <Info size={12} className="opacity-60" />
                <div className={`absolute bottom-full right-0 mb-2 w-48 p-2 rounded-lg text-[10px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-600 shadow-lg border border-slate-200'
                  }`}>
                  AI adjusted response to stay within your budget. Change in Profile → Auto Budget.
                </div>
              </div>
            </div>
          )}

          {/* Referenced Attachments Indicator */}
          {!isUser && message.referencedAttachments && message.referencedAttachments.length > 0 && (
            <div className={`flex flex-wrap gap-2 mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              <div className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                </svg>
                <span className="font-medium">Referenced: {message.referencedAttachments.join(', ')}</span>
              </div>
            </div>
          )}

          {/* Attachments (Images/Files) - Enhanced with PowerPoint support */}
          {message.attachments && message.attachments.length > 0 && (
            <div className={`flex flex-wrap gap-2 mb-2 ${isUser ? 'justify-end' : 'justify-start'}`} role="list" aria-label="Attached files">
              {message.attachments.map((att, idx) => {
                const isPPT = att.mimeType === 'application/vnd.ms-powerpoint' ||
                  att.mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                  att.name.toLowerCase().endsWith('.ppt') ||
                  att.name.toLowerCase().endsWith('.pptx');
                const isPDF = att.mimeType === 'application/pdf';

                return (
                  <div key={idx} className={`relative group overflow-hidden rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-white'}`} role="listitem">
                    {(att.mimeType || '').startsWith('image/') ? (
                      <img src={att.preview} alt={`Attachment: ${att.name}`} className="h-32 w-auto object-cover rounded-xl" />
                    ) : (
                      <div className={`h-28 w-44 flex flex-col items-center justify-center p-3 gap-2 relative ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                        {/* Preview button for PDF */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 focus-within:opacity-100">
                          {isPDF && onPreview && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onPreview(att); }}
                              className={`p-1.5 rounded-md shadow-sm border focus:outline-none focus:ring-2 focus:ring-amber-500 ${isDarkMode ? 'bg-slate-600 border-slate-500 text-slate-200 hover:text-amber-400' : 'bg-white border-slate-200 text-slate-900 hover:text-amber-500'}`}
                              title="Preview PDF"
                              aria-label={`Preview PDF: ${att.name}`}
                            >
                              <Eye size={14} />
                            </button>
                          )}
                        </div>

                        {/* File type icon with distinct styling */}
                        {isPPT ? (
                          <div className="relative">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-xs">PPT</span>
                            </div>
                            <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-amber-400 text-[8px] font-bold text-slate-900 rounded">⭐</span>
                          </div>
                        ) : isPDF ? (
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg">
                            <span className="text-white font-bold text-xs">PDF</span>
                          </div>
                        ) : (
                          <FileText size={32} className={isDarkMode ? 'text-slate-400' : 'text-slate-400'} aria-hidden="true" />
                        )}

                        {/* Filename */}
                        <span className={`text-[10px] font-medium truncate w-full text-center px-2 rounded-lg border py-1 ${isDarkMode ? 'text-slate-300 bg-slate-600/50 border-slate-500' : 'text-slate-600 bg-white border-slate-200'}`}>
                          {att.name.length > 20 ? att.name.slice(0, 17) + '...' : att.name}
                        </span>

                        {/* Action button */}
                        {isPDF && (
                          <button
                            onClick={() => onPreview && onPreview(att)}
                            className="text-[10px] font-bold text-amber-500 hover:text-amber-400 hover:underline focus:outline-none transition-colors"
                            aria-label={`Preview PDF: ${att.name}`}
                          >
                            Preview
                          </button>
                        )}
                        {isPPT && (
                          <span className="text-[9px] text-amber-400 font-medium">Premium Analysis</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Text Bubble - Dynamic Width with Fluid Animations & Persona Colors */}
          <div
            className={`
              group relative ${styles.p} ${styles.textSize} leading-relaxed 
              inline-block
              transition-all duration-150 ease-out
              ${message.isStreaming
                ? 'rounded-2xl'
                : 'rounded-2xl shadow-sm hover:shadow-md'
              }
              ${isUser
                ? 'text-white rounded-tr-none hover:-translate-y-0.5'
                : isError
                  ? 'bg-red-50 text-red-900 border-2 border-red-300 rounded-tl-none'
                  : isDarkMode
                    ? `${getPersonaColors(message.persona).darkBg} text-slate-100 rounded-tl-none backdrop-blur-sm ${message.isStreaming ? `border-l-2 ${getPersonaColors(message.persona).darkAccent}/50` : `border ${getPersonaColors(message.persona).darkBorder} shadow-lg shadow-black/20 ${getPersonaColors(message.persona).darkHoverBorder}`}`
                    : `${getPersonaColors(message.persona).lightBg} text-slate-900 rounded-tl-none ${message.isStreaming ? `border-l-2 ${getPersonaColors(message.persona).lightAccent}/50` : `border ${getPersonaColors(message.persona).lightBorder} shadow-lg ${getPersonaColors(message.persona).lightHoverBorder}`}`
              }
            `}
            style={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              // ✅ MOBILE/APK FIX: Ensure content is always visible, never clipped
              minHeight: 'auto',
              maxHeight: 'none', // Remove any max-height restrictions
              display: 'block', // Change from inline-block to block for better mobile rendering
              ...(isUser ? {
                background: `linear-gradient(135deg, ${userGradient.from}, ${userGradient.to})`,
                boxShadow: `0 4px 14px 0 ${userGradient.from}33, 0 10px 20px -4px ${userGradient.to}22`
              } : {}),
              minWidth: '120px',
              maxWidth: isUser ? '85%' : '90%',
              willChange: message.isStreaming ? 'contents' : 'transform, box-shadow'
            }}
          >
            {/* Action Buttons — hidden during streaming to reduce DOM work */}
            {!message.isStreaming && <div className={`absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0`}>
              {/* Pin Button */}
              {onTogglePin && (
                <button
                  onClick={() => onTogglePin(message.id)}
                  className={`p-1.5 rounded-lg transition-all shadow-md focus:outline-none focus:ring-2 ${isUser
                    ? isPinned
                      ? 'bg-amber-400/30 hover:bg-amber-400/40 text-white focus:ring-amber-300'
                      : 'bg-blue-400/30 hover:bg-blue-400/40 text-white focus:ring-blue-300'
                    : isPinned
                      ? isDarkMode
                        ? 'bg-amber-500/30 text-amber-400 hover:bg-amber-500/40 focus:ring-amber-500'
                        : 'bg-amber-100 text-amber-600 hover:bg-amber-200 focus:ring-amber-500'
                      : isDarkMode
                        ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-amber-400 focus:ring-amber-500'
                        : 'bg-white text-slate-600 hover:bg-amber-50 hover:text-amber-600 focus:ring-amber-500'
                    }`}
                  title={isPinned ? 'Unpin message' : 'Pin message'}
                  aria-label={isPinned ? 'Unpin message' : 'Pin message'}
                >
                  <Pin size={14} className={isPinned ? 'fill-current' : ''} />
                </button>
              )}

              {/* Copy Button for Bot Messages */}
              {!isUser && !isError && (
                <button
                  onClick={handleCopy}
                  className={`p-1.5 rounded-lg transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-amber-500 ${isDarkMode
                    ? 'text-slate-300 hover:text-amber-400 bg-slate-700 hover:bg-slate-600'
                    : 'text-slate-600 hover:text-amber-600 bg-white hover:bg-amber-50'
                    }`}
                  title="Copy response"
                  aria-label="Copy message text"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              )}

              {/* Export Button for Bot Messages */}
              {!isUser && !isError && onExport && !message.isStreaming && (
                <button
                  onClick={() => onExport(message)}
                  className={`p-1.5 rounded-lg transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode
                    ? 'text-slate-300 hover:text-blue-400 bg-slate-700 hover:bg-slate-600'
                    : 'text-slate-600 hover:text-blue-600 bg-white hover:bg-blue-50'
                    }`}
                  title="Export as document"
                  aria-label="Export response as document"
                >
                  <Download size={14} />
                </button>
              )}
            </div>}

            {isUser ? (
              <div className="whitespace-pre-wrap break-words font-medium">{message.content}</div>
            ) : (
              <div className="markdown-body">
                {/* Streaming Indicator - Fluid Typing Animation */}
                {message.isStreaming && !message.content && (
                  <div className="flex items-center gap-3 mb-3 animate-in fade-in duration-300">
                    <div className="typing-indicator flex gap-1.5">
                      <div className="typing-dot w-2.5 h-2.5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full" style={{ animationDelay: '0ms' }} />
                      <div className="typing-dot w-2.5 h-2.5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full" style={{ animationDelay: '200ms' }} />
                      <div className="typing-dot w-2.5 h-2.5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full" style={{ animationDelay: '400ms' }} />
                    </div>
                    <span className="text-xs font-semibold text-amber-500/80 animate-pulse">Thinking...</span>
                  </div>
                )}

                {/* Thinking Process sections hidden per user request
                    The AI still uses internal reasoning but it's not displayed */}

                <div className={`prose prose-base sm:prose-lg max-w-none break-words ${isDarkMode ? 'prose-invert' : ''} ${message.isStreaming ? 'streaming-content' : ''}`} style={{
                  '--tw-prose-body': isDarkMode ? '#e2e8f0' : '#1e293b',
                  '--tw-prose-headings': isDarkMode ? '#f1f5f9' : '#0f172a',
                  '--tw-prose-bold': isDarkMode ? '#fde68a' : '#000000',
                  '--tw-prose-links': isDarkMode ? '#fbbf24' : '#d97706',
                  '--tw-prose-code': isDarkMode ? '#fbbf24' : '#d97706',
                  lineHeight: '1.8'
                } as React.CSSProperties}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Support for HTML elements in markdown
                      div: ({ node, ...props }) => <div {...props} />,
                      br: ({ node, ...props }) => <br {...props} />,
                      table: ({ node, ...props }) => message.isStreaming ? (
                        <div className="overflow-x-auto my-2"><table className="w-full text-sm" {...props} /></div>
                      ) : (
                        <div className={`overflow-x-auto my-4 sm:my-6 -mx-2 sm:mx-0 rounded-xl max-w-full shadow-lg border ${isDarkMode ? 'border-slate-600/50 bg-slate-800/30' : 'border-slate-200 bg-white'}`}>
                          <table className="w-full border-collapse text-xs sm:text-sm md:text-base table-auto" {...props} />
                        </div>
                      ),
                      p: ({ node, ...props }) => <p className="my-3 sm:my-4 leading-relaxed sm:leading-loose break-words text-[13px] sm:text-sm md:text-base" {...props} />,
                      ul: ({ node, ...props }) => <ul className="my-3 sm:my-4 ml-4 sm:ml-6 space-y-1.5 sm:space-y-2.5 list-disc break-words" {...props} />,
                      ol: ({ node, ...props }) => <ol className="my-3 sm:my-4 ml-4 sm:ml-6 space-y-1.5 sm:space-y-2.5 list-decimal break-words" {...props} />,
                      li: ({ node, ...props }) => <li className="leading-relaxed sm:leading-loose break-words mb-1 sm:mb-2 text-[13px] sm:text-sm md:text-base" {...props} />,
                      h1: ({ node, ...props }) => <h1 className="text-lg sm:text-2xl md:text-3xl font-bold my-4 sm:my-6 mt-5 sm:mt-8 pb-2 border-b-2 border-amber-500/30 break-words" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-base sm:text-xl md:text-2xl font-bold my-3 sm:my-5 mt-4 sm:mt-7 pb-2 border-b border-slate-500/20 break-words" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-sm sm:text-lg md:text-xl font-bold my-3 sm:my-4 mt-4 sm:mt-6 break-words" {...props} />,
                      blockquote: ({ node, ...props }) => (
                        <blockquote className={`border-l-4 border-amber-500 pl-4 sm:pl-6 pr-3 sm:pr-4 my-4 sm:my-6 py-3 sm:py-4 italic break-words rounded-r shadow-md text-[13px] sm:text-sm md:text-base ${isDarkMode ? 'bg-amber-500/20 text-slate-100' : 'bg-amber-50 text-slate-800'}`} {...props} />
                      ),
                      code: ({ node, className, children, ...props }: any) => {
                        const match = /language-(\w+)/.exec(className || '');
                        const inline = !className;
                        // Only use code blocks for actual programming languages, not plain text
                        const isProgrammingLanguage = match && !['text', 'plain', 'txt'].includes(match[1].toLowerCase());
                        return !inline && isProgrammingLanguage ? (
                          <div className="relative my-3 sm:my-4 rounded-lg overflow-hidden bg-slate-950 max-w-full shadow-lg">
                            <div className="flex items-center justify-between px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-900 border-b border-slate-800">
                              <span className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase">{match[1]}</span>
                              <button
                                onClick={() => navigator.clipboard.writeText(String(children))}
                                className="text-[10px] sm:text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors p-1 -m-1 touch-manipulation"
                              >
                                <Copy size={12} /> Copy
                              </button>
                            </div>
                            <pre className="p-3 sm:p-4 overflow-x-auto text-[10px] sm:text-xs md:text-sm leading-relaxed max-w-full touch-pan-x">
                              <code {...props}>
                                {children}
                              </code>
                            </pre>
                          </div>
                        ) : (
                          <code className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-sm md:text-base font-mono font-semibold ${isDarkMode ? 'bg-slate-700/70 text-amber-300' : 'bg-amber-100 text-amber-800'}`} {...props}>
                            {children}
                          </code>
                        );
                      },
                      thead: ({ node, ...props }) => <thead className={`${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`} {...props} />,
                      th: ({ node, ...props }) => <th className={`px-3 sm:px-4 py-2 sm:py-2.5 text-left font-semibold border-b ${isDarkMode ? 'text-amber-300 border-slate-600' : 'text-slate-700 border-slate-300'} text-xs sm:text-sm`} {...props} />,
                      td: ({ node, children, ...props }) => {
                        // Only truncate EXTREMELY long content
                        const content = typeof children === 'string' && children.length > 500
                          ? children.substring(0, 500) + '...'
                          : children;
                        return (
                          <td
                            className={`px-3 sm:px-4 py-2 sm:py-2.5 border-b ${isDarkMode ? 'text-slate-200 border-slate-700/50' : 'text-slate-700 border-slate-200'} text-xs sm:text-sm`}
                            {...props}
                          >
                            {content}
                          </td>
                        );
                      },
                      tr: ({ node, ...props }) => <tr className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700/30 even:bg-slate-800/20' : 'hover:bg-slate-50 even:bg-slate-50/50'}`} {...props} />,
                      del: ({ node, ...props }) => <del className={`decoration-2 line-through opacity-80 ${isDarkMode ? 'text-slate-500 decoration-slate-500' : 'text-slate-400 decoration-slate-400'}`} {...props} />,
                      strong: ({ node, ...props }) => <strong className={`font-extrabold ${isDarkMode ? 'text-amber-300' : 'text-slate-900'}`} {...props} />,
                      hr: ({ node, ...props }) => <hr className={`my-8 border-t-2 ${isDarkMode ? 'border-slate-600' : 'border-slate-300'}`} {...props} />,
                      em: ({ node, ...props }) => <em className="italic not-italic font-medium" {...props} />,
                    }}
                  >
                    {content}
                  </ReactMarkdown>

                  {/* Streaming cursor indicator — uses CSS animation for GPU-only rendering */}
                  {message.isStreaming && message.content && (
                    <span className="streaming-cursor" />
                  )}
                </div>

                {chartData && renderChart()}
              </div>
            )}

            {/* Timestamp and Token Cost */}
            {showTimestamps && (
            <div className={`text-[10px] mt-1 font-medium opacity-70 text-right flex items-center justify-end gap-1.5 ${isUser ? 'text-slate-300' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {/* Persona Type (AI messages only) */}
              {!isUser && !isError && message.persona && (
                <>
                  <span className="font-bold uppercase">
                    {message.persona === 'standard' ? 'ASSISTANT' : message.persona.toUpperCase()}
                  </span>
                  <span className="opacity-50">•</span>
                </>
              )}
              {(() => {
                try {
                  const date = typeof message.timestamp === 'string' ? new Date(message.timestamp) : message.timestamp;
                  if (isNaN(date.getTime())) return ""; // Invalid date
                  const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  return `${dateStr} (${timeStr})`;
                } catch (e) {
                  return "";
                }
              })()}
              {message.tokensCharged && message.tokensCharged > 0 && (
                <>
                  <span className="opacity-50">•</span>
                  <span className="font-bold text-amber-500 opacity-90">
                    {message.tokensCharged.toLocaleString()} <span className="text-base">⚡</span>
                  </span>
                </>
              )}
            </div>
            )}

          </div>

          {/* Feedback & Regenerate Actions for Bot — hidden during streaming */}
          {!isUser && !isError && !message.isStreaming && (
            <div className="flex items-center gap-1.5 mt-1 ml-1 relative" role="group" aria-label="Feedback">
              {onFeedback && (
                <>
                  {/* Thumbs Up Button */}
                  <button
                    ref={feedbackButtonRef}
                    onClick={() => handleFeedbackClick('up')}
                    disabled={feedbackLoading !== null}
                    className={`relative p-1.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 ${message.feedback === 'up'
                      ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400 scale-110'
                      : isDarkMode
                        ? 'text-slate-400 hover:bg-slate-700 hover:text-green-400 hover:scale-110'
                        : 'text-slate-400 hover:bg-slate-100 hover:text-green-600 hover:scale-110'
                      } ${feedbackLoading === 'up' ? 'animate-pulse' : ''}`}
                    aria-label="Thumbs up - Give positive feedback"
                    aria-pressed={message.feedback === 'up'}
                    title={localRewarded ? 'Feedback submitted' : 'Give positive feedback (earn token refund!)'}
                  >
                    <ThumbsUp size={14} className={`transition-transform ${message.feedback === 'up' ? 'fill-current' : ''}`} />
                    {feedbackLoading === 'up' && (
                      <span className="absolute inset-0 rounded-full animate-ping bg-green-400/30" />
                    )}
                  </button>

                  {/* Thumbs Down Button */}
                  <button
                    onClick={() => handleFeedbackClick('down')}
                    disabled={feedbackLoading !== null}
                    className={`relative p-1.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 ${message.feedback === 'down'
                      ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400 scale-110'
                      : isDarkMode
                        ? 'text-slate-400 hover:bg-slate-700 hover:text-red-400 hover:scale-110'
                        : 'text-slate-400 hover:bg-slate-100 hover:text-red-600 hover:scale-110'
                      } ${feedbackLoading === 'down' ? 'animate-pulse' : ''}`}
                    aria-label="Thumbs down - Give negative feedback"
                    aria-pressed={message.feedback === 'down'}
                    title={localRewarded ? 'Feedback submitted' : 'Give negative feedback (earn token refund!)'}
                  >
                    <ThumbsDown size={14} className={`transition-transform ${message.feedback === 'down' ? 'fill-current' : ''}`} />
                    {feedbackLoading === 'down' && (
                      <span className="absolute inset-0 rounded-full animate-ping bg-red-400/30" />
                    )}
                  </button>

                  {/* Reward hint for unrewarded messages */}
                  {!localRewarded && !message.feedback && message.tokensCharged && message.tokensCharged > 0 && (
                    <span className={`text-[10px] flex items-center gap-0.5 ml-1 ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'
                      }`}>
                      <Zap size={10} />
                      <span>Earn refund</span>
                    </span>
                  )}

                  {/* Inline Reward Animation */}
                  {showRewardAnimation && rewardAmount > 0 && (
                    <div className="absolute left-0 -top-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold shadow-lg ${isDarkMode
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-amber-100 text-amber-700 border border-amber-300'
                        }`}>
                        <Sparkles className="w-3 h-3 animate-pulse" />
                        <span>+{rewardAmount}</span>
                        <Zap className="w-3 h-3" />
                        <span>refunded!</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Regenerate Button (Only for last message) */}
              {isLast && onRegenerate && (
                <button
                  onClick={onRegenerate}
                  className={`p-1.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 ml-1 hover:scale-110 ${isDarkMode
                    ? 'text-slate-400 hover:bg-slate-700 hover:text-amber-400'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-amber-600'
                    }`}
                  title="Regenerate response"
                  aria-label="Regenerate response"
                >
                  <RefreshCw size={14} />
                </button>
              )}
            </div>
          )}

          {/* Customization Hint - Only show for AI messages */}
          {!isUser && !isError && onAIPersonaClick && (
            <div className={`mt-1 ml-1 text-[9px] sm:text-[10px] italic opacity-60 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Click avatar to change persona
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

// Optimize with React.memo to prevent unnecessary re-renders
// Only re-render if message content, pin status, or feedback changes
export default React.memo(ChatMessage, (prevProps, nextProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.tokenCost === nextProps.message.tokenCost &&
    prevProps.message.tokensCharged === nextProps.message.tokensCharged &&
    prevProps.isPinned === nextProps.isPinned &&
    prevProps.hasBeenRewarded === nextProps.hasBeenRewarded &&
    prevProps.isLast === nextProps.isLast &&
    prevProps.density === nextProps.density &&
    prevProps.isDarkMode === nextProps.isDarkMode &&
    prevProps.showTimestamps === nextProps.showTimestamps &&
    prevProps.showAvatars === nextProps.showAvatars &&
    // Check for quiz data changes to ensure button renders
    JSON.stringify(prevProps.message.quiz) === JSON.stringify(nextProps.message.quiz)
  );
});
